import { useBranchLogo } from '@/entities/Branch';
import type {
  TableCell,
  TableElement,
  ImageProperties,
  FormElement,
  HeaderProperties,
} from '../model/form.types';
import { DEFAULT_HEADER_MAIN_LABEL, DEFAULT_HEADER_MARGIN } from './element-defaults';

export { migrateTableHeaderToHeader } from '@record-fill';

export function createHeaderElement(shortId: string): FormElement {
  return {
    id: `header-${shortId}`,
    type: 'HEADER',
    position: 0,
    properties: {
      name: `header_${shortId}`,
      mainLabel: DEFAULT_HEADER_MAIN_LABEL,
      rq: '',
      rev: '',
      title: '',
      marginByPage: { 0: { ...DEFAULT_HEADER_MARGIN } },
    } as HeaderProperties,
  };
}

export function useHeaderTableFactory() {
  const { currentLogoUrl } = useBranchLogo();

  function createHeaderTable(shortId: string): TableElement {
    const tableId = `header-table-${shortId}`;
    const rows = 2;
    const columns = 4;
    const cells: TableCell[][] = [];

    // Create all cells first
    for (let row = 0; row < rows; row++) {
      const rowCells: TableCell[] = [];
      for (let col = 0; col < columns; col++) {
        rowCells.push({
          id: `cell-${shortId}-${row}-${col}`,
          rowIndex: row,
          colIndex: col,
          rowSpan: 1,
          colSpan: 1,
          merged: false,
        });
      }
      cells.push(rowCells);
    }

    // Row 1, Col 1: ImageElement with branch logo (merged cells: rows 0-1, col 0)
    const logoMergeGroupId = `merge-logo-${shortId}`;
    const logoElementId = `image-${shortId}`;
    cells[0][0].content = {
      id: logoElementId,
      type: 'IMAGE',
      position: 0,
      properties: {
        name: `logo_${shortId}`,
        imageUrl: currentLogoUrl.value,
        allowChange: true,
      } as ImageProperties,
    };
    // Root cell for logo merge (row 0, col 0)
    cells[0][0].rowSpan = 2;
    cells[0][0].colSpan = 1;
    cells[0][0].isMerged = false;
    cells[0][0].mergeGroupId = logoMergeGroupId;
    // Merged cell (row 1, col 0)
    cells[1][0].isMerged = true;
    cells[1][0].merged = true;
    cells[1][0].mergeGroupId = logoMergeGroupId;
    cells[1][0].rowSpan = 0;
    cells[1][0].colSpan = 0;
    cells[1][0].mergeRoot = { row: 0, col: 0 };

    // Row 1, Col 2: FixedTextElement with "REGISTRO DA QUALIDADE"
    const fixedTextId = `fixed-text-${shortId}`;
    cells[0][1].content = {
      id: fixedTextId,
      type: 'FIXED_TEXT',
      position: 0,
      properties: {
        name: `registro_qualidade_${shortId}`,
        content: 'REGISTRO DA QUALIDADE',
        textAlign: 'center',
        fontSize: 16,
        fontWeight: 'semibold',
      },
    };

    // Row 1, Col 3: TextInputElement for RQ
    const rqElementId = `rq-${shortId}`;
    cells[0][2].content = {
      id: rqElementId,
      type: 'TEXT_INPUT',
      position: 0,
      properties: {
        name: `rq_${shortId}`,
        label: 'RQ',
        labelPosition: 'left',
        placeholder: '00.00',
        editable: true,
        regex: '^\\d*\\.?\\d*$',
      },
    };
    cells[0][2].properties = {
      padding: 4,
    };

    // Row 1, Col 4: TextInputElement for REV
    const revElementId = `rev-${shortId}`;
    cells[0][3].content = {
      id: revElementId,
      type: 'TEXT_INPUT',
      position: 0,
      properties: {
        name: `rev_${shortId}`,
        label: 'REV.',
        labelPosition: 'left',
        placeholder: '00',
        editable: true,
        regex: '^\\d*\\.?\\d*$',
      },
    };
    cells[0][3].properties = {
      padding: 4,
    };

    // Row 2, Col 2-4: TextInputElement for title (merged cells: row 1, cols 1-3)
    const titleMergeGroupId = `merge-title-${shortId}`;
    const titleElementId = `title-${shortId}`;
    cells[1][1].content = {
      id: titleElementId,
      type: 'TEXT_INPUT',
      position: 0,
      properties: {
        name: `titulo_registro_${shortId}`,
        placeholder: 'Título do Registro',
        editable: true,
      },
    };
    // Root cell for title merge (row 1, col 1)
    cells[1][1].rowSpan = 1;
    cells[1][1].colSpan = 3;
    cells[1][1].isMerged = false;
    cells[1][1].mergeGroupId = titleMergeGroupId;
    // Merged cells (row 1, cols 2-3)
    cells[1][2].isMerged = true;
    cells[1][2].merged = true;
    cells[1][2].mergeGroupId = titleMergeGroupId;
    cells[1][2].rowSpan = 0;
    cells[1][2].colSpan = 0;
    cells[1][2].mergeRoot = { row: 1, col: 1 };
    cells[1][3].isMerged = true;
    cells[1][3].merged = true;
    cells[1][3].mergeGroupId = titleMergeGroupId;
    cells[1][3].rowSpan = 0;
    cells[1][3].colSpan = 0;
    cells[1][3].mergeRoot = { row: 1, col: 1 };
    cells[1][1].properties = {
      padding: 4,
    };

    // Fractional units (fr) mirroring the QA physical header spec's 4/7/3/3cm
    // columns (17cm total) — same ratio, so the WYSIWYG ruler matches the
    // printed/PDF header built from QUALITY_HEADER_STYLES on the server.
    const columnWidths = [4, 7, 3, 3];

    // Create MergedCell entries for proper merge/unmerge functionality
    const mergedCells = [
      {
        id: logoMergeGroupId,
        startRow: 0,
        startCol: 0,
        endRow: 1,
        endCol: 0,
        cells: [cells[0][0].id, cells[1][0].id],
      },
      {
        id: titleMergeGroupId,
        startRow: 1,
        startCol: 1,
        endRow: 1,
        endCol: 3,
        cells: [cells[1][1].id, cells[1][2].id, cells[1][3].id],
      },
    ];

    return {
      id: tableId,
      type: 'TABLE',
      position: 0,
      properties: {
        name: `header_table_${shortId}`,
        rows,
        columns,
        cells,
        columnWidths,
        mergedCells,
        tableWidth: 100,
        borderWidth: 1,
        isHeader: true,
      },
    };
  }

  return {
    createHeaderTable,
  };
}
